import { Controller } from '@hotwired/stimulus'
import { useMeta } from 'stimulus-use'
import Dropzone from "dropzone"
import { client } from "@/axios"

Dropzone.autoDiscover = false

export default class extends Controller {
    static values = {
        server: Array,
    }

    static targets = [
        'input',
        'loaded',
        'removeButton',
    ]

    connect() {
        useMeta(this)
        this.dropZone = this.createDropZone()
        this.bindEvents()
        this.inputTarget.disabled = true
    }

    disconnect() {
        this.dropZone.destroy()
    }

    bindEvents() {
        const removeButtonTemplate = this.removeButtonTarget.innerHTML.trim();

        this.dropZone.on('addedfile', (e) => {
            if (this.dropZone.files.length > this.dropZone.options.maxFiles) {
                this.dropZone.removeFile(e);
            }

            const removeButton = Dropzone.createElement(removeButtonTemplate);

            removeButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.dropZone.removeFile(e);
            });

            e.previewElement.appendChild(removeButton);
        });

        this.dropZone.on("addedfile", (file) => {
            setTimeout(() => { file.accepted && (new UploadController(this, file)).upload() }, 0)
        })

        this.dropZone.on("removedfile", (file) => {
            file.controller && removeElement(file.controller.hiddenInput)
        })

        this.dropZone.on("canceled", (file) => {
            file.controller && file.controller.abort()
        })

        this.dropZone.on("processing", (file) => {
            this.submitButton.disabled = true
        })

        this.dropZone.on("queuecomplete", (file) => {
            this.submitButton.disabled = false
        })
    }

    updateMaxFilesReachedClass() {
        if (
            this.dropZone.options.maxFiles != null &&
            this.dropZone.getAcceptedFiles().length >= this.dropZone.options.maxFiles
        ) {
            if (this.dropZone.getAcceptedFiles().length === this.dropZone.options.maxFiles) {
                this.dropZone.emit("maxfilesreached", this.files);
            }
            this.element.classList.add("dz-max-files-reached");
        } else {
            this.element.classList.remove("dz-max-files-reached");
        }
    }

    createDropZone() {
        return new Dropzone(this.element, {
            url: this.url,
            headers: this.headers,
            maxFiles: this.maxFiles,
            maxFilesize: this.maxFileSize,
            acceptedFiles: this.acceptedFiles,
            addRemoveLinks: false,
            dictDefaultMessage: "Прикрепить фотографию",
            dictRemoveFile: 'Удалить',
            dictFileTooBig: 'Файл слишком большой ({{filesize}}MiB). Максимальный размер: {{maxFilesize}}MiB.',
            dictInvalidFileType: 'Нельзя загружать файл данного типа',
            dictCancelUpload: 'Отменить',
            dictUploadCanceled: 'Отменино',
            dictMaxFilesExceeded: 'Нельзя добалять ещё файлы',
            dictCancelUploadConfirmation: 'Вы уверены, что хотите отменить эту загрузку?',
            autoQueue: false,
            init: () => {
                setTimeout(() => {
                    this.serverValue.forEach(({id, size, type, name, imageUrl}) => {
                        const mockFile = {
                            upload: {
                                progress: 1,
                                total: size,
                                bytesSent: size,
                                uuid: Dropzone.uuidv4(),
                                chunked: false,
                            },
                            id,
                            accepted: true,
                            fromServer: true,
                            status: Dropzone.ACCEPTED,
                            name,
                            size,
                            type,
                            imageUrl,
                            dataURL: imageUrl,
                        }
                
                        this.dropZone.files.push(mockFile)
                        this.dropZone.emit('addedfile', mockFile)
                        this.dropZone.emit('complete', mockFile)
                        this.dropZone.createThumbnailFromUrl(
                            mockFile,
                            this.dropZone.options.thumbnailWidth,
                            this.dropZone.options.thumbnailHeight,
                            this.dropZone.options.thumbnailMethod,
                            this.dropZone.options.fixOrientation,
                            (thumbnail) => {
                                this.dropZone.emit("thumbnail", mockFile, thumbnail);
                            },
                            true
                        )
                        this.updateMaxFilesReachedClass()
                    })
                }, 1000);
            }
        })
    }

    get headers() {
        const token = document.head.querySelector(`meta[name="csrf-token"]`)
        return { "X-CSRF-Token": token?.getAttribute('content') }
    }

    get url() { return this.data.get("url") }

    get maxFiles() { return this.data.get("maxFiles") || 1 }

    get maxFileSize() { return this.data.get("maxFileSize") || 5 }

    get acceptedFiles() { return this.data.get("acceptedFiles") }

    get addRemoveLinks() { return this.data.get("addRemoveLinks") || true }

    get form() { return this.element.closest("form") }

    get submitButton() { return this.form.querySelector("input[type=submit], button[type=submit]") }
}

class UploadController {
    constructor(controller, file) {
        this.controller = controller
        this.file = file
        this.file.controller = this
    }

    async upload() {
        this.hiddenInput = this.createHiddenInput()

        if (this.file?.fromServer) {
            this.emmitSuccess()

            this.hiddenInput.value = this.file.id
            return
        }

        const formData = new FormData()
        formData.append("file", this.file)

        this.abortController = new AbortController()

        this.emmitUploading()

        try {
            const { data: { id } } = await client({
                method: 'post',
                url: this.controller.url,
                data: formData,
                onUploadProgress: (event) => {
                    const progress = event.loaded / event.total * 100
                    this.file.previewTemplate.querySelector(".dz-upload").style.width = `${progress}%`
                },
                signal: this.abortController.signal
            })

            this.hiddenInput.value = id
            this.emmitSuccess()
        } catch (error) {
            this.emmitError()
        }
    }

    emmitUploading() {
        this.file.status = Dropzone.UPLOADING
        this.controller.dropZone.emit("processing", this.file)
    }

    emmitSuccess() {
        this.file.status = Dropzone.SUCCESS
        this.controller.dropZone.emit("success", this.file)
        this.controller.dropZone.emit("complete", this.file)
    }

    emmitError() {
        this.file.status = Dropzone.ERROR
        this.controller.dropZone.emit("error", this.file, error)
        this.controller.dropZone.emit("complete", this.file)
    }

    abort() {
        this.abortController.abort()
    }

    createHiddenInput() {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.controller.inputTarget.name
        insertAfter(input, this.controller.inputTarget)
        return input
    }
}

function removeElement(el) {
    if (el && el.parentNode) {
        el.parentNode.removeChild(el);
    }
}

function insertAfter(el, referenceNode) {
    return referenceNode.parentNode.insertBefore(el, referenceNode.nextSibling);
}